import { OWS_TYPE, NG_LAYER_TYPES, RELATIONSHIP_TYPES } from '../../../constants';
import { arraySortAscendingFunction } from '../../../services/utilities';
import { getCombinedOlId } from '../../../services/theme';
import { isSmallDevice } from '../../../services/utilities/devices';

export const getters = {
    getLayerById: (state) => (id) => {
        return state.layers.find(layer => layer.id === id);
    },
    getLayerByName: (state) => (name) => {
        return state.layers.find(layer => layer.data.title === name);
    },
    getSourceById: (state) => (id) => {
        const result = state.sources.find(source => source.id === id);
        return result;
    },
    getLayerRelations: (state) => (id) => {
        const layerData = state.layers.find(layer => layer.id === id);
        return layerData.relations ? layerData.relations : [];
    },
    // Get relations
    // filter out all layers that do not have DisplayInTable attributes.
    // Only perform this check if the target is a themelayer
    getLayerRelationsWithDisplayAttributes: (state, _getters) => (id) => {
        return _getters.getLayerRelations(id).filter(
            (relation) => relation.type === RELATIONSHIP_TYPES.Table2Table
                || relation.type === RELATIONSHIP_TYPES.ThemeLayer2Table
                || _getters.getLayerByOriginalId(
                    relation.targetThemeLayerId
                )?.extended?.informationAttributes?.some(
                    (i) => i.displayInTable
                )
        );
    },
    getTableResultRelations: (state, _getters) => (id) => {
        const tableRelations = _getters.getTableSearchResultById(id)?.relations ?? [];
        const layerRelations = state.layers
            .map((layer) => ({
                layerTitle: layer.data.title,
                targetRelations: layer.relations?.filter(
                    ({ targetThemeLayerId, type }) => targetThemeLayerId === id && type === RELATIONSHIP_TYPES.ThemeLayer2Table
                )
            }))
            .filter(({ targetRelations }) => targetRelations?.length > 0)
            .map((relation) => ({
                targetRelations: relation.targetRelations.map((tr) => ({
                    ...tr,
                    name: `${relation.layerTitle} (${tr.name})`
                }))
            }))
            .flatMap(({ targetRelations }) => targetRelations);
        return tableRelations.concat(layerRelations).sort(arraySortAscendingFunction('name'));
    },
    getLayerByOriginalId: (state) => (id) => {
        return state.layers.find(layer => layer.data.id === id);
    },
    getActivatedLayers: state => {
        return state.layers.filter(layer => layer.type !== NG_LAYER_TYPES.BASE && layer.active);
    },
    getActivatedDefaultLayers: state => {
        return state.layers.filter(layer => layer.type !== NG_LAYER_TYPES.BASE && layer.type !== NG_LAYER_TYPES.CUSTOM && layer.active);
    },
    getActivatedWFSLayers: state => {
        return state.layers.filter(layer => layer.type !== NG_LAYER_TYPES.BASE && layer.active && layer.extended.serviceType === OWS_TYPE.WFS);
    },
    getSearchableWFSLayers: (state, _getters) => {
        let activatedWFSLayers;
        // In mobile only select activatedWFSLayers because the info we need is not loaded for non active layers
        if (_getters.isMobile) {
            activatedWFSLayers = _getters.getActivatedWFSLayers;
        } else {
            // In web select all WFS layers
            activatedWFSLayers = state.layers.filter(layer => layer.type !== NG_LAYER_TYPES.BASE && layer.extended.serviceType === OWS_TYPE.WFS);
        }

        const searchableWFSLayers = activatedWFSLayers.filter((layer) => {

            let hasTableAttributes = false;
            const hasFilterAttributes = !!layer?.filters?.data?.length > 0;

            // For mobile we need to get displayInTable attributes
            if (_getters.isMobile) {
                hasTableAttributes =
                    !!layer?.extended?.informationAttributes?.some(
                        (i) => i.displayInTable
                    );
            } else {
                const webTheme = _getters.getWebTheme;

                // For web we show result in table when showTableView is set or
                // when showTableViewSmallDevices is set on a small device
                // Otherwise we need a popup attribute.
                if (
                    webTheme.data.showTableView &&
                    (!isSmallDevice() ||
                        webTheme.data.showTableViewSmallDevices)
                ) {
                    hasTableAttributes =
                        !!layer?.extended?.informationAttributes?.some(
                            (i) => i.displayInTable
                        );
                } else {
                    hasTableAttributes =
                        !!layer?.extended?.informationAttributes?.some(
                            (i) => i.popup
                        );
                }
            }
            // Only show search when there are filterattributes and there are table attributes.
            return hasFilterAttributes && hasTableAttributes;

        }).map((layer) => ({
            id: layer.id, // Subset of properties
            title: layer.extended.title,
            active: true
        }));

        // Add searchable layers that are not already active from loaded theme data.
        const themes = _getters.getThemes;
        let searchableThemeLayers = [];

        themes.forEach(theme => {
            searchableThemeLayers.push(...theme.data.themeLayers
                .filter(layer => layer.showInAdvancedSearch)  // Filter by showInAdvancedSearch = true
                .map(layer => ({
                    id: `${theme.data.id}-${layer.id}`,  // Combine data id and themeLayer id
                    title: layer.title,
                    active: false
                })));

        });

        if (searchableThemeLayers.length > 0) {
            // Combine arrays
            const combinedArray = searchableWFSLayers.concat(searchableThemeLayers);

            // Remove duplicates by `id`
            const uniqueArray = combinedArray.filter((value, index, self) =>
                index === self.findIndex((item) => item.id === value.id)
            );
            return uniqueArray;
        } else {
            return searchableWFSLayers;
        }

    },
    getBaseLayers: state => {
        return state.layers.filter(layer => layer.type === NG_LAYER_TYPES.BASE).sort((a, b) => a.data.order - b.data.order);
    },
    getImportedLayers: state => {
        return state.layers.filter(layer => layer.type === NG_LAYER_TYPES.CUSTOM).sort((a, b) => a.data.order - b.data.order);
    },
    // for BRK
    anyParcelLayerEnabled: (state, getters) => {
        const themeOrFeedback = state.layers
            .filter(layer => layer.type === 'theme' && layer.active)
            .find(getters.isWFSParcelLayer);
        return themeOrFeedback !== undefined;
    },
    firstParcelLayerEnabled: (state, getters) => {
        return state.layers
            .filter(layer => layer.type === 'theme' && layer.active)
            .find(getters.isWFSParcelLayer);
    },
    getFirstParcelLayerId: (state, getters) => {
        let found = null;
        state.themes.forEach((theme) => {
            if (!found) {
                const layer = theme.data.themeLayers.find(getters.isWFSParcelLayer);
                if (layer) {
                    found = {
                        themeId: theme.id,
                        layerId: layer.id,
                        combinedOlId: getCombinedOlId(theme.id, layer.id)
                    };
                }
            }
        });
        return found;
    },
    // get all themelayers that have the searchable flag
    getSearchableLayers: state => {
        let searchableLayers = [];
        state.themes.forEach((theme) => {
            const themelayers = theme.data.themeLayers.filter(tl => tl.searchableAttributes.length > 0);
            searchableLayers = searchableLayers.concat(themelayers);
        });
        searchableLayers.forEach((layer) => {
            layer.searchable = true;
            layer.searchableAttributes = [...new Set(layer.searchableAttributes)].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        });

        // get all feedbacks that have a searchable attribute
        const searchableFeedbacks = state.feedbacks.filter(f => f.data.feedbackType.feedbackFields.some(ff => ff.searchType));

        // for each searchable and active feedback
        // create a list of searchable attributes
        // create a themelayer
        // add to list of searchable layers
        searchableFeedbacks.forEach(feedback => {
            const attributes = feedback.data.feedbackType.feedbackFields.filter(ff => ff.searchType).map(ff => ff.name);

            let themeLayer;
            // if the feedback contains a themelayer use that
            if (feedback.data.themeLayers.length > 0) {
                themeLayer = { ...feedback.data.themeLayers[0] };
            } else {
                // otherwise use themeLayerId and themeLayerTitle from initial API call
                themeLayer = {
                    id: feedback.data.feedbackType.themeLayerId,
                    title: feedback.data.feedbackType.themeLayerTitle
                };

            }
            themeLayer.searchable = true;
            themeLayer.searchableAttributes = [...new Set(attributes)].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

            searchableLayers = searchableLayers.concat(themeLayer);
        });
        return searchableLayers.sort(arraySortAscendingFunction('title'));
    },
    // Get value hidePolygonFill for layer with id (in this case a customId)
    getLayerhidePolygonFillById: (state) => (id) => {
        let hidePolygonFill = false;
        const layer = state.layers.find(l => l.id === id);
        if (layer?.hidePolygonFill) {
            hidePolygonFill = layer.hidePolygonFill;
        }
        return hidePolygonFill;
    }
};
